import {
  Card,
  Text,
  Box,
  InlineStack,
  Button,
  Tooltip,
  Badge,
  BlockStack,
  SkeletonBodyText,
} from "@medentic/polaris-react";
import { EditIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import NotesModal, { NoteTone } from "./NotesModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { Tone } from "@medentic/polaris-react/build/ts/src/components/Badge";

interface NotesConfig {
  apiEndpoint: string;
  apiMethod: "PATCH" | "PUT";
  noteField: string;
  toneField: string;
  queryKeys: string[][];
}

interface NotesBaseProps {
  notes?: string;
  tone?: NoteTone;
  loading?: boolean;
  toneOptions: Array<{
    value: NoteTone;
    label: string;
    description: string;
  }>;
  onDataChange?: (updates: Record<string, string>) => void;
  asCard?: boolean;
  i18nKeyPrefix?: string;
  modalI18nKeyPrefix?: string;
}

type NotesProps =
  | (NotesBaseProps & {
      config: NotesConfig;
      noteField?: never;
      toneField?: never;
    })
  | (NotesBaseProps & {
      config?: undefined;
      noteField: string;
      toneField: string;
    });

function Notes({
  notes = "",
  tone = "default",
  loading = false,
  config,
  toneOptions,
  onDataChange,
  asCard = true,
  i18nKeyPrefix = "patientDetails",
  modalI18nKeyPrefix = "patientDetails",
  noteField,
  toneField,
}: NotesProps) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editedNotes, setEditedNotes] = useState(notes);
  const [editedTone, setEditedTone] = useState(tone);

  const { PATCH, PUT } = useApiRequest();
  const queryClient = useQueryClient();

  const { mutate: updateNotesMutation, isPending: isSaving } = useMutation({
    mutationFn: (data: Record<string, string>) => {
      if (!config) {
        return Promise.reject(new Error("Notes config is missing."));
      }
      const apiCall = config.apiMethod === "PATCH" ? PATCH : PUT;
      return apiCall(config.apiEndpoint, data);
    },
    onSuccess: () => {
      if (!config) {
        return;
      }
      // Invalidate all specified query keys
      config.queryKeys.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
      setIsModalOpen(false);

      // Update parent component data if callback provided
      if (onDataChange) {
        onDataChange({
          [config.noteField]: editedNotes,
          [config.toneField]: editedTone,
        });
      }
    },
    onError: (error) => {
      console.error("Error updating notes:", error);
    },
  });

  if (loading) {
    return asCard ? (
      <Card>
        <SkeletonBodyText lines={3} />
      </Card>
    ) : (
      <SkeletonBodyText lines={3} />
    );
  }

  const handleOpenModal = () => {
    setEditedNotes(notes);
    setEditedTone(tone);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleNotesChange = (value: string) => {
    setEditedNotes(value);
  };

  const handleToneChange = (value: NoteTone) => {
    setEditedTone(value);
  };

  const handleSave = () => {
    const resolvedNoteField = config?.noteField ?? noteField;
    const resolvedToneField = config?.toneField ?? toneField;

    if (!resolvedNoteField || !resolvedToneField) {
      return;
    }

    if (config) {
      updateNotesMutation({
        [resolvedNoteField]: editedNotes,
        [resolvedToneField]: editedTone,
      });
      return;
    }

    if (onDataChange) {
      onDataChange({
        [resolvedNoteField]: editedNotes,
        [resolvedToneField]: editedTone,
      });
    }
    setIsModalOpen(false);
  };

  const getToneColor = (tone: NoteTone): Tone | undefined => {
    switch (tone) {
      case "attention":
        return "info";
      case "warning":
        return "attention";
      case "critical":
        return "critical";
      default:
        return undefined;
    }
  };

  const renderNoteContent = () => {
    if (!notes) {
      return (
        <Text as="p" variant="bodyMd" tone="subdued">
          {t(`${i18nKeyPrefix}.noNotes`)}
        </Text>
      );
    }

    return (
      <Box>
        <Badge tone={getToneColor(tone)}>{notes}</Badge>
      </Box>
    );
  };

  const content = (
    <>
      <BlockStack gap="400">
        <InlineStack align="space-between">
          <Text variant="headingMd" as="h2">
            {t(`${i18nKeyPrefix}.notes.title`)}
          </Text>
          <Tooltip
            content={
              notes
                ? t(`${i18nKeyPrefix}.notes.editTooltip`)
                : t(`${i18nKeyPrefix}.notes.addTooltip`)
            }
          >
            <Button
              icon={EditIcon}
              onClick={handleOpenModal}
              variant="plain"
              accessibilityLabel={
                notes
                  ? t(`${i18nKeyPrefix}.actions.editNotes`)
                  : t(`${i18nKeyPrefix}.actions.addNotes`)
              }
            />
          </Tooltip>
        </InlineStack>

        {!notes && (
          <Text variant="bodySm" as="p" tone="subdued">
            {t(`${i18nKeyPrefix}.notes.description`)}
          </Text>
        )}

        {renderNoteContent()}
      </BlockStack>

      <NotesModal
        open={isModalOpen}
        onClose={handleCloseModal}
        notes={editedNotes}
        tone={editedTone}
        onNotesChange={handleNotesChange}
        onToneChange={handleToneChange}
        onSave={handleSave}
        toneOptions={toneOptions}
        isSaving={config ? isSaving : false}
        i18nKeyPrefix={modalI18nKeyPrefix}
      />
    </>
  );

  return asCard ? <Card>{content}</Card> : content;
}

export default Notes;
